import React from 'react';
import { StyledTriggerPoint, styles } from './ActionBar.styles';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useActionBarController } from '@apps/guest/packages/layout-engine/components/ActionBar/ActionBar.controller';
import { ActionBarTransition } from '@apps/guest/packages/layout-engine/components/ActionBar/ActionBarTransition';
import { TelemetryProvider } from './ActionBar.telemetry';
import { useLayout } from '../../layouts/LayoutProvider';
import { FixedActionButtonContainer } from './FixedActionButtonContainer';
import { ActionButton } from './ActionButton';
import { useTranslation } from '@shared/core';
import GuestGuideModal from './GuestGuideModal';

type ActionBarProps = Readonly<{
  eventId: string;
  showAlways?: boolean;
}>;

const ActionBarContent: React.FC<Omit<ActionBarProps, 'eventId'>> = ({ showAlways = false }) => {
  const {
    isTriggerPoint,
    triggerPointRef,
    shouldDisplayRSVPButton,
    onUnlockDialogHandle,
    shouldDisplayUnlockButton,
    rsvpButtonText,
    onRsvpHandle,
    isStreamDisplayed,
    onVirtualEventBlockClicked,
    unlockButtonText,
    streamEvent,
    isActionBarHidden,
    showEventDate,
    isGuestGuideExperimentEnabled,
    guestGuideSessionData,
  } = useActionBarController();

  const { tq } = useTranslation('actionBar');

  return (
    <>
      {isGuestGuideExperimentEnabled ? (
        <GuestGuideModal sessionData={guestGuideSessionData} />
      ) : (
        !isActionBarHidden && (
          <ActionBarTransition show={isTriggerPoint || showAlways}>
            <FixedActionButtonContainer
              alignment={'center'}
              {...(!showEventDate && !isStreamDisplayed && { paddingX: 5 })}
            >
              {isStreamDisplayed ? (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  gap={3}
                >
                  <div
                    style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(206, 54, 54, 0.4)',
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#CE3636',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                    />
                  </div>
                  <TextV2
                    typographyVariant="button1"
                    color="mono15"
                    whiteSpace="nowrap"
                  >
                    {tq`Now Live`}
                  </TextV2>
                </Flex>
              ) : (
                showEventDate && (
                  <TextV2
                    typographyVariant="button1"
                    color="mono15"
                    whiteSpace="nowrap"
                  >
                    {showEventDate}
                  </TextV2>
                )
              )}

              {isStreamDisplayed ? (
                streamEvent.isLinkValid && (
                  <ActionButton onClick={onVirtualEventBlockClicked}>
                    <TextV2
                      typographyVariant="button1"
                      color="mono15"
                      whiteSpace="nowrap"
                    >
                      {tq`Join Livestream`}
                    </TextV2>
                  </ActionButton>
                )
              ) : (
                <>
                  {shouldDisplayUnlockButton && (
                    <ButtonV2
                      shape="rounded"
                      onClick={onUnlockDialogHandle}
                      intent="neutral"
                      height={8}
                    >
                      {unlockButtonText}
                    </ButtonV2>
                  )}
                  {shouldDisplayRSVPButton && (
                    <ButtonV2
                      shape="rounded"
                      intent="neutral"
                      onClick={onRsvpHandle}
                      height={8}
                    >
                      {rsvpButtonText}
                    </ButtonV2>
                  )}
                </>
              )}
            </FixedActionButtonContainer>
          </ActionBarTransition>
        )
      )}

      <StyledTriggerPoint
        __css={styles.triggerPoint}
        ref={triggerPointRef}
      />
    </>
  );
};

const ActionBar: React.FC<ActionBarProps> = ({ eventId, showAlways }) => {
  const { layout } = useLayout();
  return (
    <TelemetryProvider context={{ eventId, layout }}>
      <ActionBarContent showAlways={showAlways} />
    </TelemetryProvider>
  );
};

ActionBar.displayName = 'ActionBar';

export { ActionBar };
